import { Link } from "@tanstack/react-router";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { formatArticle } from "@/content/format";
import {
	GENERATION_STEPS,
	isGenerating,
	type ProductSummary,
} from "@/content/schema";
import { fr } from "@/i18n/fr";
import { StatusBadge } from "./StatusBadge";

interface GenerationStateProps {
	product: ProductSummary;
}

/** Shown instead of the 3D viewer while the guide of a product is not ready. */
export function GenerationState({ product }: GenerationStateProps) {
	const status = product.generation_status;
	const running = isGenerating(status);
	const step = (GENERATION_STEPS as readonly string[]).indexOf(status) + 1;
	const message = running
		? fr.generationRunning
		: status === "failed"
			? fr.generationFailed
			: fr.generationPending;
	return (
		<div className="flex min-h-full items-center justify-center p-4 md:p-8">
			<section className="grid w-full max-w-lg gap-4 rounded-lg border border-line bg-bg-elev p-6">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<h1 className="font-semibold text-[22px] leading-7 tracking-tight">
							{product.name}
						</h1>
						<p className="mt-0.5 text-[13px] text-fg-muted leading-[18px]">
							{product.designation} · {formatArticle(product.ikea_id)}
						</p>
					</div>
					<StatusBadge status={status} />
				</div>

				<p className="text-[17px] leading-[26px]">{message}</p>
				{status === "failed" && product.generation_error && (
					<p className="rounded-sm bg-danger-soft px-3 py-2 font-mono text-danger text-xs">
						{product.generation_error}
					</p>
				)}

				{running && (
					<div>
						<div className="mb-1 flex justify-between text-fg-muted text-xs">
							<span>{fr.generationStatus[status]}</span>
							<span>{fr.generationStep(step, GENERATION_STEPS.length)}</span>
						</div>
						<div
							role="progressbar"
							aria-label={fr.generationStatus[status]}
							aria-valuemin={0}
							aria-valuemax={GENERATION_STEPS.length}
							aria-valuenow={step}
							className="flex h-1.5 gap-[3px]"
						>
							{GENERATION_STEPS.map((name, i) => (
								<span
									key={name}
									className={`h-full flex-1 rounded-sm transition-colors ${i < step ? "bg-accent" : "bg-bg-sunken"}`}
								/>
							))}
						</div>
					</div>
				)}

				<div className="flex flex-wrap gap-2">
					{product.assembly_guide_url && (
						<a
							className="ui-btn ui-btn-sm no-underline"
							href={product.assembly_guide_url}
							target="_blank"
							rel="noreferrer"
						>
							{fr.ikeaGuide}
							<ArrowUpRight className="size-4" aria-hidden="true" />
						</a>
					)}
					<a
						className="ui-btn-ghost ui-btn-sm no-underline"
						href={product.product_url}
						target="_blank"
						rel="noreferrer"
					>
						{fr.ikeaPage}
						<ArrowUpRight className="size-4" aria-hidden="true" />
					</a>
					<Link to="/catalogue" className="ui-btn-ghost ui-btn-sm no-underline">
						<ChevronLeft className="size-4" aria-hidden="true" />
						{fr.catalog}
					</Link>
				</div>
			</section>
		</div>
	);
}
